// Story 8.6 (ADO-2015), D-8/D-9 — sending the cart once, and holding its confirmation.
//
// ONE submit action (`submit`) for every Submit button on the page: the Submit card and the Order
// footer (Story 8.14, Figma 4978:2213) both call it, through `useSubmitOrder`. The store's
// per-cart `customerOrderReference` is what the send carries; the footer's reference field
// writes it through `setCustomerOrderReference`. Story 8.4 (ADO-2013): a cart is the depot AND
// the Trading Account, so two accounts at one Value Centre hold a reference each.
//
// A refused reference (the typed `customer-order-reference` 400,
// `CUSTOMER_ORDER_REFERENCE_PROBLEM_TYPE`) is bound to the field: `error.field` names it, the
// footer shows the message there, and editing the reference retires the refusal (back to
// `idle`). The key survives an edit of the reference after a retryable failure: if the first
// attempt did commit, the replay answers with the order as stored, reference included (D-9).
//
// One attempt at a time. Each attempt carries one `Idempotency-Key` (a UUID minted when it
// starts): a network failure, a timeout (`SUBMIT_ORDER_TIMEOUT_MS`), a 5xx or a transient
// 408/425/429 keeps the key, so the next send replays the SAME attempt and the server answers
// with the first send's confirmation rather than making a second order. Only these retryable
// failures offer "Try again". Enabler E8-G (ADO-3109), DA-1: the key is kept for the revision the
// attempt sent; once the basket is at another revision (a line write landed, or a re-read of the
// cart moved it), the send is a new attempt. DA-3: the server records every attempt
// that names a cart under its key, refusals included, and answers a replay of the key with what
// it recorded. So a refused reference ends the attempt (a missing required one is recorded, and
// its replay would be refused forever), and so does every other 4xx; the next send mints a new
// key. Only a 400 the server refuses before it names a cart (a malformed request, a refused
// fulfilment choice) keeps the key, since nothing was recorded under it; the Submit button
// replays it once the Shopper has fixed what it named.
//
// A `cart-changed` 409 (`CART_CHANGED_PROBLEM_TYPE` in the `x-shoplink-problem-type` header)
// means the cart the page holds is not the one the server would send: it names another cart, or
// the cart is empty. The basket stores are then reset and the depot's current cart re-opened
// and re-hydrated, so the page shows it and the next Submit sends it. Every other 409 (no Trading
// Account, mixed currencies, nothing orderable, another contributor's cart, a depot not set up)
// is one no re-open can fix, so it leaves the basket as it is and only shows its message.
// Story 8.3 (ADO-2012), Task 13.2: a `cart-sending` 409 (another send holds the cart) is tried
// once more with the same key after the line writes' wait (`submitOnceMoreIfHeld`); a second one
// is shown as the refusal it is.
// A refusal's message is retired once the Shopper changes the cart (a line edit starts, a
// previous order is being added, or the cart is being cleared); a retryable failure stays, with
// its key.
//
// A send is refused while a line edit is still settling (`useBasketStore.pending`), a previous
// order is being added or the cart is being cleared (Story 8.9's `clearing`), and while the active
// basket is not open: the cart the Shopper reviewed must be the cart that is sent.
//
// Enabler E8-G (ADO-3109), DA-1: every send carries the revision of the basket on screen
// (`seenRevision`), or the one the caller names (the /choose "Submit now", which has no cart view
// and names the revision the open-carts list showed). A send without one, or with one the cart has
// moved past, is refused as card 09. When the caller names a revision the basket store does not
// hold, the store's lines are not what was shown: that refusal marks no line and is not sent again.
//
// On success the confirmation is held for the depot. The cart is reused (AC8): the send kept its
// id, deleted its lines and moved its revision on, so the revision the stores hold is stale. The
// basket stores are reset and the active basket re-opened (get-or-open answers with the same
// cart) and re-hydrated (`reopenActiveBasket`, shared with the `cart-changed` path), so the next
// write carries the revision the send moved the cart to. `open` short-circuits when already
// `ready` for the same depot, so the reset comes first. The confirmation is cleared on a change of
// cart (the depot or, Story 8.4, the account), and once the Shopper fills the cart again (a line
// lands in the basket). A page reload shows the empty cart; the order is in /orders.
//
// Enabler E8-G (ADO-3109), AC16: the cart keeps the reference and the requested delivery date of
// its latest accepted send, and the cart read carries them. The per-cart reference and fulfilment
// choice stay the edit buffer: when a read of a cart lands and the buffer holds nothing for that
// cart, the reference is seeded from what the cart kept, and so is the requested delivery date,
// once the cart's fulfilment options are known and only while they still offer that date.
// Story 8.15 (ADO-2024), D-5: a read of an armed cart seeds them from the arming instead (its
// reference, `''` for none, and its fulfilment choice), where the buffer holds nothing.
//
// Story 8.13 (ADO-2022), D-7: the fulfilment choice, per cart (`cartTargetKey`): every read and
// write is keyed by the cart, never the depot. Held means touched: the Fulfilment card writes what
// the Shopper changes through `setFulfilmentField` (`fulfilmentByCart`), and registers what it
// SHOWS, defaults included, through `setFulfilmentShown` while it is on screen. A send carries
// what the card shows when it shows one (the /cart Submit buttons); otherwise only what was
// touched, without the fields of the other method (the /choose "Submit now": nothing touched, no
// fulfilment fields). Either way it carries nothing unless the cart's latest options offer the
// choice (`fulfilmentOfferedFor`): not offered, or the read failed and the card is hidden (D-15).
// A refused choice (the typed `order-fulfilment` 400, `ORDER_FULFILMENT_PROBLEM_TYPE`) binds each
// message to its field (`error.fields`), records what the send carried (`error.fulfilmentSent`),
// keeps the key, and re-reads the cart's options (D-2: the calendar then refreshes). A refusal
// because the cart offers no choice also forgets the cart's touched fields and its options, so the
// card hides and the next send carries none. A held choice belongs to the cart as it was before
// its next send (decided 2026-10-16, for Story 8.3's shared cart): it is dropped for the sent cart
// on success, and when this browser learns a colleague sent the cart (the basket store's
// `cartSentAt`, from card 10 or card 11; Enabler E8-G, ADO-3109, DA-1), so the emptied cart starts
// with the card's defaults. Editing a refused field retires that field's message (the attempt
// goes back to `idle` once none is left). The choice survives `clear()`, as the reference does.
//
// ADO-3257 (story D-4, D-5): card 09's cart carries the cart's latest accepted send. When it was
// made after the revision the refused send carried (`sentSinceSeen`), a colleague sent the cart:
// card 09 names when (`changedWhileReviewing.sent`), the basket store records it (`cartSentAt`,
// which clears the held choice, AC3), and nothing is sent, not even card 09's one resend for a cart
// with nothing to mark. A read of the cart that names such a send does the same through the
// basket store. This tab's own accepted send is remembered as seen, so the read after it names
// nothing.

import { create } from 'zustand';
import {
  CART_CHANGED_PROBLEM_TYPE,
  CART_CHANGED_WHILE_REVIEWING_PROBLEM_TYPE,
  CUSTOMER_ORDER_REFERENCE_PROBLEM_TYPE,
  ORDER_FULFILMENT_PROBLEM_TYPE,
  OrderSubmissionRefusedError,
  submitOrder,
  type FulfilmentField,
  type FulfilmentRequestFields,
  type InvalidFulfilmentField,
  type OrderConfirmation,
  type SubmitOrderRequest,
} from '../api/orders.client';
import { useSessionStore } from '../auth/useSessionStore';
import { useActiveBasketStore } from './useActiveBasketStore';
import {
  CART_SENDING_PROBLEM,
  type AutoSendChoices,
  type AutoSendState,
} from '../api/basket.client';
import { conflictView } from './conflictCopy';
import { useBasketStore, waitForTheSendHold } from './useBasketStore';
import {
  fulfilmentOfferedFor,
  offeredDeliveryDatesFor,
  refreshFulfilmentOptions,
  subscribeToFulfilmentOptions,
} from './useFulfilmentOptions';
import { cartCallTargetAt, cartTargetAt, sessionCartKey } from './sessionCart';
import { cartTargetBody, cartTargetKey, sameCartTarget } from '../api/cart-target';
import { useOnOrderStore } from '../onOrder/useOnOrderStore';
import { sentSinceSeen } from './sentSinceSeen';
import { rememberShownRevision } from './shownCartRevision';

export type OrderSubmissionPhase = 'idle' | 'sending' | 'sent' | 'failed';

/** The copy shown when the send could not reach the server or it failed (retryable). */
export const SUBMIT_UNAVAILABLE_MESSAGE =
  'We could not send your order. Check your connection and try again.';

/** How long a send may take before it is abandoned as a retryable failure. Set above one server
 *  send transaction's budget (`SUBMISSION_TRANSACTION_TIMEOUT_MS` 30 s plus
 *  `SUBMISSION_TRANSACTION_MAX_WAIT_MS` 5 s, in order-management's confirmed-order write
 *  repository; the web cannot import them), so a normal slow send is not cut off mid-write. An
 *  abandoned send keeps its key, so if the server did commit it, the retry replays that order. */
export const SUBMIT_ORDER_TIMEOUT_MS = 45_000;

export interface OrderSubmissionError {
  readonly message: string;
  readonly retryable: boolean;
  /** Set when the server refused the customer order reference: the page shows the message on
   *  that field rather than in the failure banner. */
  readonly field?: 'customerOrderReference';
  /** Story 8.13 (ADO-2022): set when the server refused the fulfilment choice, each message
   *  bound to its field; the page shows them on the fields rather than in the failure banner. */
  readonly fields?: readonly InvalidFulfilmentField[];
  /** Story 8.13 (ADO-2022): with `fields`, the fulfilment fields the refused send carried, so a
   *  refusal of a value no longer shown (an untouched default the options moved) can be retired. */
  readonly fulfilmentSent?: FulfilmentRequestFields;
  /** Story 8.3 (ADO-2012), Task 13.1 (card 09): set when the send was refused because the cart
   *  changed while the Shopper reviewed it; the page shows card 09's banner instead of the
   *  failure message, and marks the lines (the basket store's conflicts). ADO-3257 (story D-5):
   *  `sent` when the cart was sent after the revision the send reviewed: when (ISO 8601 UTC), and
   *  when the refusal was noted (epoch ms), so the banner works out "today" without a clock. */
  readonly changedWhileReviewing?: {
    readonly lineCount: number;
    readonly sent?: { readonly sentAt: string; readonly notedAt: number };
  };
}

interface Attempt {
  readonly key: string;
  readonly depotId: string;
  readonly cartId: string;
  /** Enabler E8-G (ADO-3109), DA-1: the revision the attempt sent. A reused cart keeps its id, so
   *  only the revision tells the basket the attempt sent from one the Shopper changed since. */
  readonly seenRevision: number;
}

interface OrderSubmissionState {
  phase: OrderSubmissionPhase;
  /** The depot the current attempt or confirmation is for. */
  depotId: string | null;
  /** Story 8.13 (ADO-2022): the cart (`cartTargetKey`) the current attempt or confirmation is
   *  for, so a refused fulfilment choice is read and retired by the cart. */
  cartKey: string | null;
  confirmation: OrderConfirmation | null;
  /** The failure of the last attempt: its message, and whether retrying the same attempt helps. */
  error: OrderSubmissionError | null;
  /** The store's own order reference to send, per cart (`cartTargetKey`: the depot and the
   *  account), as the Order footer's field writes it. */
  customerOrderReferenceByCart: Readonly<Record<string, string>>;
  /** Set the reference the next send of the cart at `depotId` (the session's account there)
   *  carries ('' for none). Retires a refusal of the reference at that depot. */
  setCustomerOrderReference: (depotId: string, value: string) => void;
  /** Story 8.13 (ADO-2022), D-7: the fulfilment fields the Shopper touched, per cart. */
  fulfilmentByCart: Readonly<Record<string, FulfilmentRequestFields>>;
  /** What the Fulfilment card shows, defaults included, per cart, while it is on screen. */
  fulfilmentShownByCart: Readonly<Record<string, FulfilmentRequestFields>>;
  /** Touch one fulfilment field of the cart `cartKey` (`undefined` forgets it). Retires a
   *  refusal of that field on that cart. */
  setFulfilmentField: (cartKey: string, field: FulfilmentField, value: string | undefined) => void;
  /** Register what the card shows for the cart `cartKey` (`null` once it is gone). Keyed by the
   *  card's own cart, so a change of cart at the same depot (8.4) registers and clears the cart
   *  it was showing, not the one the session holds when the call lands. */
  setFulfilmentShown: (cartKey: string, shown: FulfilmentRequestFields | null) => void;
  /** Story 8.15 (ADO-2024), D-5: per cart, how many changes the Shopper made to its reference or
   *  fulfilment choice (`setCustomerOrderReference`, `setFulfilmentField`). An armed cart's
   *  re-save follows these alone: a seed of the buffers (from what the cart kept, or from its
   *  arming) and a change the store makes itself are not the Shopper's edits. */
  shopperEditsByCart: Readonly<Record<string, number>>;
  /** Retire the cart's refusals of `fields` (the card no longer shows the field, or shows another
   *  value than the refused send carried): their messages are moot. Idle again once no field is
   *  refused. */
  retireFulfilmentRefusals: (cartKey: string, fields: readonly FulfilmentField[]) => void;
  /** THE submit action, shared by every Submit button: send the active basket at `depotId` with
   *  that cart's reference, at the revision of the basket on screen, or at `seenRevision` when
   *  the caller names one (Enabler E8-G, ADO-3109, DA-1: the /choose "Submit now"). A no-op while
   *  an attempt is in flight or the basket is not in a sendable state (see
   *  {@link canSubmitOrder}). */
  submit: (depotId: string, options?: { readonly seenRevision?: number }) => Promise<void>;
  /** Forget the attempt and the confirmation. */
  clear: () => void;
}

/** Bumped by every `clear()`: a send that resolves after it settles nothing. */
let clearGeneration = 0;
/** The attempt a retryable failure left, replayed by the next `submit` for the same cart. */
let pendingAttempt: Attempt | null = null;
/** Story 8.3 (ADO-2012), card 09: the carts whose one resend, after a refusal with nothing to
 *  mark, is in flight, so a second such refusal of that cart is shown rather than sent again.
 *  Per cart, so another cart's resend never stops this one's. */
const resendingUnchanged = new Set<string>();

/** Story 8.3 (ADO-2012), card 09 (D-24): the lines card 09 highlights on the review page, which
 *  its banner counts: the marks of a refused send that show a message. A mark made elsewhere
 *  (cards 01 to 05) and a removal both made (OQ-3a, shown nowhere) are not counted. */
function linesCard09Highlights(): number {
  let count = 0;
  for (const conflict of useBasketStore.getState().lineConflicts.values()) {
    if (conflict.action === 'review' && conflictView(conflict).kind !== 'none') count += 1;
  }
  return count;
}

/** The key the reference of the cart at `depotId` is held under. */
export function referenceKeyAt(depotId: string): string {
  return cartTargetKey(cartTargetAt(depotId));
}

/** The active basket's cart id when it is open for the cart at `depotId` (the depot and the
 *  session's account there; Story 8.4, ADO-2013), else null. */
function openCartId(depotId: string): string | null {
  const active = useActiveBasketStore.getState();
  if (active.status !== 'ready' || !sameCartTarget(active.cart, cartTargetAt(depotId))) {
    return null;
  }
  return active.basket?.cartId ?? null;
}

/** True when a send may start now at `depotId`: no attempt in flight, the active basket is open
 *  for that depot, and no basket write is settling. */
export function canSubmitOrder(depotId: string | null): boolean {
  if (depotId === null) return false;
  if (useOrderSubmissionStore.getState().phase === 'sending') return false;
  const basket = useBasketStore.getState();
  if (basket.pending.size > 0 || basket.addingFromOrder || basket.clearing) return false;
  return openCartId(depotId) !== null;
}

/**
 * Drop the cart the basket stores hold and open the depot's current one (get-or-open: after a send,
 * the same cart, reused), then load its lines, so the stores hold the cart's revision now. `open`
 * short-circuits when already `ready` for the cart, so the reset comes first.
 * The lines are loaded only while the attempt that started at `startedIn` is still current and
 * the active basket is still this depot's: a depot switch mid-open (which `clear()`s this store)
 * must not load the old depot's lines over the new depot's.
 */
function reopenActiveBasket(depotId: string, startedIn: number): void {
  const cart = cartCallTargetAt(depotId);
  useBasketStore.getState().reset();
  useActiveBasketStore.getState().reset();
  // Enabler E8-G (ADO-3109), DA-10: a staff user with no store account has no cart to open.
  if (cart === null) return;
  void useActiveBasketStore
    .getState()
    .open(cart)
    .then(() => {
      if (startedIn !== clearGeneration) return;
      if (!sameCartTarget(useActiveBasketStore.getState().cart, cart)) return;
      return useBasketStore.getState().hydrate(cart);
    });
}

/** `submitOrder`, abandoned after {@link SUBMIT_ORDER_TIMEOUT_MS}: the request is aborted and the
 *  promise rejects with a plain Error (the caller's retryable path). The race, not only the
 *  abort, bounds the wait, so a stall before the fetch starts (the access-token read in
 *  `apiFetch`) is bounded too. */
async function submitWithTimeout(
  request: SubmitOrderRequest,
  idempotencyKey: string,
): Promise<OrderConfirmation> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error('The order send timed out'));
    }, SUBMIT_ORDER_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      submitOrder(request, idempotencyKey, { signal: controller.signal }),
      timedOut,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

const COLLECTION_FIELDS: readonly FulfilmentField[] = [
  'collectionDate',
  'collectionTime',
  'contactName',
];

/** Story 8.13 (ADO-2022), D-7: the fulfilment fields a send carries: none unless the cart's
 *  latest options offer the choice (D-15); then what the card shows, else only what was touched,
 *  without the fields of the other method. */
function fulfilmentFor(
  state: Pick<OrderSubmissionState, 'fulfilmentByCart' | 'fulfilmentShownByCart'>,
  cartKey: string,
): FulfilmentRequestFields {
  if (!fulfilmentOfferedFor(cartKey)) return {};
  const shown = state.fulfilmentShownByCart[cartKey];
  if (shown !== undefined) return shown;
  const touched = state.fulfilmentByCart[cartKey] ?? {};
  const collection = touched.fulfilmentMethod === 'COLLECTION';
  return Object.fromEntries(
    Object.entries(touched).filter(([field, value]) => {
      if (value === undefined) return false;
      if (collection) return field !== 'deliveryDate';
      return !COLLECTION_FIELDS.includes(field as FulfilmentField);
    }),
  );
}

/** `fields` with `field` set to `value`, or without it when `value` is undefined. The method
 *  takes only a known method; the card offers no other. */
function withFulfilmentField(
  fields: FulfilmentRequestFields,
  field: FulfilmentField,
  value: string | undefined,
): FulfilmentRequestFields {
  const next: { -readonly [Field in FulfilmentField]?: FulfilmentRequestFields[Field] } = {
    ...fields,
  };
  delete next[field];
  if (value === undefined) return next;
  if (field === 'fulfilmentMethod') {
    if (value === 'DELIVERY' || value === 'COLLECTION') next.fulfilmentMethod = value;
  } else {
    next[field] = value;
  }
  return next;
}

/** `edits` with one more of the Shopper's edits counted for the cart `cartKey`. */
function withOneMoreEdit(
  edits: Readonly<Record<string, number>>,
  cartKey: string,
): Record<string, number> {
  return { ...edits, [cartKey]: (edits[cartKey] ?? 0) + 1 };
}

/** `record` without `key`. */
function without<T>(record: Readonly<Record<string, T>>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([entry]) => entry !== key));
}

/** Story 8.3 (ADO-2012), Task 13.2: a send that meets the cart held by another send
 *  (`cart-sending`) tries once more with the same key after the line writes' wait: by then that
 *  send has closed the cart, and the fence answers with the order it placed. A second
 *  `cart-sending` is the caller's, shown as the refusal it is. */
async function submitOnceMoreIfHeld(
  request: SubmitOrderRequest,
  idempotencyKey: string,
  stillCurrent: () => boolean,
): Promise<OrderConfirmation> {
  try {
    return await submitWithTimeout(request, idempotencyKey);
  } catch (error) {
    const held =
      error instanceof OrderSubmissionRefusedError && error.problemType === CART_SENDING_PROBLEM;
    if (!held) throw error;
    await waitForTheSendHold();
    // A switch of cart or a sign-out during the wait: the refusal stands, and the caller drops it.
    if (!stillCurrent()) throw error;
    return submitWithTimeout(request, idempotencyKey);
  }
}

/** The reference to send under `cartKey`, trimmed, or undefined when there is none. */
function referenceFor(references: Readonly<Record<string, string>>, cartKey: string) {
  const value = references[cartKey]?.trim() ?? '';
  return value === '' ? undefined : value;
}

/** Story 8.15 (ADO-2024), D-5: what an Auto-send armed (or re-saved) on Order Review carries for
 *  the cart `cartKey`: the reference and the fulfilment fields a send from the review would carry
 *  now. The reference is always carried, `''` when the field is blank, so the server stores what
 *  the review shows and never falls back to the reference the cart kept; a fulfilment field is
 *  absent for none. */
export function autoSendChoicesFor(cartKey: string): AutoSendChoices {
  const state = useOrderSubmissionStore.getState();
  return {
    customerOrderReference: referenceFor(state.customerOrderReferenceByCart, cartKey) ?? '',
    ...fulfilmentFor(state, cartKey),
  };
}

export const useOrderSubmissionStore = create<OrderSubmissionState>((set, get) => ({
  phase: 'idle',
  depotId: null,
  cartKey: null,
  confirmation: null,
  error: null,
  customerOrderReferenceByCart: {},
  fulfilmentByCart: {},
  fulfilmentShownByCart: {},
  shopperEditsByCart: {},

  setFulfilmentField(cartKey, field, value) {
    const state = get();
    const touched = withFulfilmentField(state.fulfilmentByCart[cartKey] ?? {}, field, value);
    // Editing a refused field retires its message (not `clear()`: the kept key makes the fixed
    // resend safe); the attempt is idle again once no field is refused.
    const refusedFields =
      state.phase === 'failed' && state.cartKey === cartKey ? state.error?.fields : undefined;
    const remaining = refusedFields?.filter((refusal) => refusal.field !== field);
    const refusalChange =
      refusedFields === undefined ||
      remaining === undefined ||
      remaining.length === refusedFields.length
        ? {}
        : remaining.length === 0
          ? { phase: 'idle' as const, error: null }
          : { error: { ...(state.error as OrderSubmissionError), fields: remaining } };
    set({
      fulfilmentByCart: { ...state.fulfilmentByCart, [cartKey]: touched },
      shopperEditsByCart: withOneMoreEdit(state.shopperEditsByCart, cartKey),
      ...refusalChange,
    });
  },

  setFulfilmentShown(cartKey, shown) {
    const state = get();
    set({
      fulfilmentShownByCart:
        shown === null
          ? without(state.fulfilmentShownByCart, cartKey)
          : { ...state.fulfilmentShownByCart, [cartKey]: shown },
    });
  },

  retireFulfilmentRefusals(cartKey, fields) {
    const state = get();
    const refusedFields =
      state.phase === 'failed' && state.cartKey === cartKey ? state.error?.fields : undefined;
    if (refusedFields === undefined) return;
    const remaining = refusedFields.filter((refusal) => !fields.includes(refusal.field));
    if (remaining.length === refusedFields.length) return;
    set(
      remaining.length === 0
        ? { phase: 'idle', error: null }
        : { error: { ...(state.error as OrderSubmissionError), fields: remaining } },
    );
  },

  setCustomerOrderReference(depotId, value) {
    const state = get();
    // Editing the reference retires a refusal of it at this depot: its message was about the
    // reference as it was. Not `clear()`, which would drop the rest of the send's state; the
    // refusal already ended its attempt (story DA-3), so the fixed send mints a new key.
    const refusalRetired =
      state.phase === 'failed' &&
      state.depotId === depotId &&
      state.error?.field === 'customerOrderReference';
    const cartKey = referenceKeyAt(depotId);
    set({
      customerOrderReferenceByCart: { ...state.customerOrderReferenceByCart, [cartKey]: value },
      shopperEditsByCart: withOneMoreEdit(state.shopperEditsByCart, cartKey),
      ...(refusalRetired ? { phase: 'idle' as const, error: null } : {}),
    });
  },

  async submit(depotId, options) {
    if (!canSubmitOrder(depotId)) return;
    const cartId = openCartId(depotId);
    if (cartId === null) return;

    // Story 8.3 (ADO-2012), Task 13.1 (card 09, D-24): the revision of the cart on screen and the
    // lines the Shopper reviewed, so a cart that changed since is refused and its changes marked.
    // Enabler E8-G (ADO-3109), DA-1: always sent, since a reused cart keeps its id and only the
    // revision tells a stale page; before the cart is read it is the empty basket's 0. A caller
    // with no cart view of its own names the revision it was shown instead. The basket's lines are
    // what was reviewed only at the revision the basket holds: for a named revision it does not
    // hold (a read of the cart landed before or after the one listed), no lines were reviewed here.
    const shown = useBasketStore.getState();
    const seenRevision = options?.seenRevision ?? shown.revision;
    const reviewed = seenRevision === shown.revision ? shown.lines : null;

    // Enabler E8-G (ADO-3109), DA-1: a retry replays the attempt only at the revision it sent. A
    // basket changed since (an add into the cart the attempt emptied, card 11, or a re-read of
    // it) is another send under a new key: the old key would replay the first order.
    const attempt: Attempt =
      pendingAttempt !== null &&
      pendingAttempt.depotId === depotId &&
      pendingAttempt.cartId === cartId &&
      pendingAttempt.seenRevision === seenRevision
        ? pendingAttempt
        : { key: crypto.randomUUID(), depotId, cartId, seenRevision };
    pendingAttempt = attempt;
    const startedIn = clearGeneration;
    // Story 8.3 (ADO-2012): the basket store's generation too, which a sign-out or a re-open of
    // the cart bumps without clearing this store.
    const basketStartedIn = useBasketStore.getState().generation();
    const stillCurrent = () =>
      startedIn === clearGeneration && basketStartedIn === useBasketStore.getState().generation();
    const cart = cartTargetAt(depotId);
    const cartKey = cartTargetKey(cart);
    const customerOrderReference = referenceFor(get().customerOrderReferenceByCart, cartKey);
    const fulfilment = fulfilmentFor(get(), cartKey);
    set({ phase: 'sending', depotId, cartKey, error: null });

    try {
      const confirmation = await submitOnceMoreIfHeld(
        {
          // Story 8.3 (ADO-2012), D-1: the cart is keyed by the depot and the account.
          ...cartTargetBody(cart),
          cartId,
          ...(customerOrderReference !== undefined ? { customerOrderReference } : {}),
          seenRevision,
          ...fulfilment,
        },
        attempt.key,
        stillCurrent,
      );
      // Story 8.12 (ADO-2021, D-6): the sent products are now on order, every contributor's lines
      // included (the send carries all sections), so the whole on-order cache is read again.
      // Before the generation check: a depot switch mid-send drops the confirmation, but the
      // order is on the server and the on-order cache is Customer-wide, so it survives the switch.
      void useOnOrderStore.getState().invalidateAll();
      // ADO-3257 (story D-4): this tab saw its own send go. The send was taken at `seenRevision`
      // and left the cart past it, so a later read, which names this send, names nothing; a later
      // send is taken at a revision at or past this one and is still named. Before the generation
      // check, as above: the order is on the server even when the tab left the cart mid-send.
      rememberShownRevision(cartId, seenRevision + 1);
      if (startedIn !== clearGeneration) return;
      pendingAttempt = null;
      // The sent reference belongs to the sent order; the emptied cart's next read seeds the
      // buffer from what the cart kept (AC16).
      const otherReferences = Object.fromEntries(
        Object.entries(get().customerOrderReferenceByCart).filter(([key]) => key !== cartKey),
      );
      set({
        phase: 'sent',
        depotId,
        cartKey,
        confirmation,
        error: null,
        customerOrderReferenceByCart: otherReferences,
        // Story 8.13 (ADO-2022), D-7: the sent choice belongs to the sent order too.
        fulfilmentByCart: without(get().fulfilmentByCart, cartKey),
      });

      // The send emptied the cart and moved its revision on (AC8): read it again.
      reopenActiveBasket(depotId, startedIn);
    } catch (error) {
      const refused = error instanceof OrderSubmissionRefusedError;
      // Story 8.12 (ADO-2021, D-6): only a refusal proves nothing was placed. A timeout, a 5xx or
      // a lost connection may follow an accepted order, and a re-read is harmless if it did not.
      if (!refused) void useOnOrderStore.getState().invalidateAll();
      if (startedIn !== clearGeneration) return;
      // Story 8.3 (ADO-2012): the basket store alone was reset (a sign-out, a re-open of the cart),
      // so the answer is for a cart no longer on screen. This attempt still holds `phase` (no other
      // send starts while it is 'sending'), so it frees it and shows nothing.
      if (!stillCurrent()) {
        set({ phase: 'idle', error: null });
        return;
      }
      // Enabler E8-G (ADO-3109), DA-3: a refusal recorded under the key would be the answer to
      // every replay of it, so it ends the attempt: a refused reference and every 4xx but a 400
      // refused before the send named a cart, which records nothing.
      if (
        refused &&
        (error.status !== 400 || error.problemType === CUSTOMER_ORDER_REFERENCE_PROBLEM_TYPE)
      ) {
        pendingAttempt = null;
      }
      // Story 8.3 (ADO-2012), card 09: show the cart as it is now and mark what changed. With no
      // lines reviewed here, the cart now stands for them, so it is shown and nothing is marked.
      if (
        refused &&
        error.problemType === CART_CHANGED_WHILE_REVIEWING_PROBLEM_TYPE &&
        error.changedCart !== undefined
      ) {
        // ADO-3257 (story D-5): the cart was sent after the revision this send reviewed.
        const sentAt = sentSinceSeen({ cartId, revision: seenRevision }, error.changedCart);
        const lineCount = useBasketStore
          .getState()
          .markChangedWhileReviewing(
            reviewed ?? error.changedCart.lines,
            error.changedCart,
            basketStartedIn,
          );
        // The basket was reset since the send started: the refusal is for a cart no longer shown.
        if (lineCount === null) {
          set({ phase: 'idle', error: null });
          return;
        }
        // Before card 09 is held: the store's `cartSentAt` clears the held choice (AC3).
        if (sentAt !== null) useBasketStore.getState().noteCartSent(sentAt, basketStartedIn);
        // Nothing to mark (decided with the user): the revision moved but the cart is what the
        // Shopper reviewed, as when a colleague changed a line and changed it back. It is sent
        // once more, at the revision now on screen; a second such refusal shows card 09. Not when
        // no lines were reviewed here: nothing says the cart now is the one the caller was shown.
        // Never after a send (ADO-3257, AC1): the lines on screen are a new order nobody reviewed.
        if (
          lineCount === 0 &&
          reviewed !== null &&
          sentAt === null &&
          !resendingUnchanged.has(cartId)
        ) {
          set({ phase: 'idle' });
          resendingUnchanged.add(cartId);
          try {
            await get().submit(depotId);
          } finally {
            resendingUnchanged.delete(cartId);
          }
          return;
        }
        // The banner counts every highlighted line, earlier marks included (the lifetime rule).
        // With none highlighted it would ask to check lines that show nothing, so the send's
        // refusal is shown as any other is.
        // A named send is card 09 whatever is highlighted (ADO-3257, AC1).
        const highlighted = linesCard09Highlights();
        const changedWhileReviewing =
          sentAt !== null
            ? { lineCount: highlighted, sent: { sentAt, notedAt: Date.now() } }
            : highlighted > 0
              ? { lineCount: highlighted }
              : null;
        set({
          phase: 'failed',
          error: {
            message: error.message,
            retryable: false,
            ...(changedWhileReviewing === null ? {} : { changedWhileReviewing }),
          },
        });
        return;
      }
      set({
        phase: 'failed',
        error: {
          message: refused ? error.message : SUBMIT_UNAVAILABLE_MESSAGE,
          retryable: !refused,
          ...(refused && error.problemType === CUSTOMER_ORDER_REFERENCE_PROBLEM_TYPE
            ? { field: 'customerOrderReference' as const }
            : {}),
          ...(refused && error.problemType === ORDER_FULFILMENT_PROBLEM_TYPE
            ? { fields: error.invalidFields, fulfilmentSent: fulfilment }
            : {}),
        },
      });
      // Story 8.13 (ADO-2022), D-2: a refused choice re-reads the cart's options. The only
      // refusal of the method the web can meet is that the cart offers no choice: its touched
      // fields and options are then forgotten, so the card hides and a resend carries none.
      if (refused && error.problemType === ORDER_FULFILMENT_PROBLEM_TYPE) {
        const notOffered = error.invalidFields.some(
          (refusal) => refusal.field === 'fulfilmentMethod',
        );
        if (notOffered) set({ fulfilmentByCart: without(get().fulfilmentByCart, cartKey) });
        refreshFulfilmentOptions(cart, { drop: notOffered });
      }
      // The page's cart is not the one the server would send: show (and next send) the current one.
      if (refused && error.problemType === CART_CHANGED_PROBLEM_TYPE) {
        reopenActiveBasket(depotId, startedIn);
      }
    }
  },

  clear() {
    clearGeneration += 1;
    pendingAttempt = null;
    set({ phase: 'idle', depotId: null, cartKey: null, confirmation: null, error: null });
  },
}));

// A change of cart (a depot switch, or Story 8.4's change of account at the same depot)
// forgets the attempt and the confirmation (the useBasketStore precedent).
useSessionStore.subscribe((session, previousSession) => {
  if (sessionCartKey(session) !== sessionCartKey(previousSession)) {
    useOrderSubmissionStore.getState().clear();
  }
});

/** Enabler E8-G (ADO-3109), AC16: per cart key, the requested delivery date a read of the cart
 *  said it kept, waiting for the cart's fulfilment options to say whether it is still on offer. */
const keptDeliveryDateByCart = new Map<string, string>();

// Story 8.3 (ADO-2012), Tasks 13.4 and 13.5 (cards 10 and 11); Enabler E8-G (ADO-3109), DA-1: the
// basket learnt the cart was sent (a change refused as made on the sent cart, or an add that went
// into the emptied cart). The cart is reused (AC8), so the active basket stays open on it and the
// next send names the cart on screen at the revision now shown; nothing is re-opened.
useBasketStore.subscribe((basket, previousBasket) => {
  const orderSentNow = basket.orderSent !== null && previousBasket.orderSent === null;
  const sentNow = basket.cartSentAt !== null && basket.cartSentAt !== previousBasket.cartSentAt;
  // Story 8.3 (ADO-2012): the reviewed cart was sent, so card 09 goes with it.
  const submission = useOrderSubmissionStore.getState();
  if ((orderSentNow || sentNow) && submission.error?.changedWhileReviewing !== undefined) {
    useOrderSubmissionStore.setState({ phase: 'idle', error: null });
  }
  // Story 8.13 (ADO-2022), decided 2026-10-16: the held choice belonged to the cart as it was
  // before the send, so the emptied cart starts with the card's defaults. So did a date an earlier
  // read said the cart kept, still waiting for the options (AC16): it is the date of the send
  // before this one.
  const cartKey = sessionCartKey();
  if (!sentNow || cartKey === null) return;
  keptDeliveryDateByCart.delete(cartKey);
  if (submission.fulfilmentByCart[cartKey] !== undefined) {
    useOrderSubmissionStore.setState((state) => ({
      fulfilmentByCart: without(state.fulfilmentByCart, cartKey),
    }));
  }
});

/** Enabler E8-G (ADO-3109), AC16: seed the cart's requested delivery date from what it kept, once
 *  its options are known: only while nothing is held for the cart and the options still offer
 *  the date. Settled either way once the options are known. */
function seedKeptDeliveryDate(cartKey: string): void {
  const date = keptDeliveryDateByCart.get(cartKey);
  if (date === undefined) return;
  const offered = offeredDeliveryDatesFor(cartKey);
  if (offered === null) return;
  keptDeliveryDateByCart.delete(cartKey);
  const state = useOrderSubmissionStore.getState();
  if (!offered.includes(date) || state.fulfilmentByCart[cartKey] !== undefined) return;
  useOrderSubmissionStore.setState({
    fulfilmentByCart: { ...state.fulfilmentByCart, [cartKey]: { deliveryDate: date } },
  });
}

/** Story 8.15 (ADO-2024), D-5: the fulfilment fields an armed Auto-send holds, as the
 *  Fulfilment card holds a touched choice; a field the arming holds none of is left to the card. */
function armedFulfilment(armed: AutoSendState): FulfilmentRequestFields | null {
  const { fulfilmentMethod, deliveryDate, collectionDate, collectionTime, contactName } = armed;
  if (fulfilmentMethod === null) return null;
  const instructions =
    armed.specialInstructions === null ? {} : { specialInstructions: armed.specialInstructions };
  if (fulfilmentMethod === 'COLLECTION') {
    return {
      fulfilmentMethod,
      ...(collectionDate === null ? {} : { collectionDate }),
      ...(collectionTime === null ? {} : { collectionTime }),
      ...(contactName === null ? {} : { contactName }),
      ...instructions,
    };
  }
  return { fulfilmentMethod, ...(deliveryDate === null ? {} : { deliveryDate }), ...instructions };
}

/** Story 8.15 (ADO-2024), D-5: seed the review of the cart `cartKey` from its armed Auto-send
 *  where the Shopper holds nothing for it: the reference (`''` for none, never the one the cart
 *  kept) and, when the arming holds one, the fulfilment choice. The review then shows what the
 *  send will carry, and its re-save carries the armed choices on with the Shopper's edit. */
function seedArmedChoices(cartKey: string, armed: AutoSendState): void {
  const state = useOrderSubmissionStore.getState();
  const fulfilment = armedFulfilment(armed);
  const seedReference = state.customerOrderReferenceByCart[cartKey] === undefined;
  const seedFulfilment = fulfilment !== null && state.fulfilmentByCart[cartKey] === undefined;
  if (!seedReference && !seedFulfilment) return;
  useOrderSubmissionStore.setState({
    ...(seedReference
      ? {
          customerOrderReferenceByCart: {
            ...state.customerOrderReferenceByCart,
            [cartKey]: armed.customerOrderReference ?? '',
          },
        }
      : {}),
    ...(seedFulfilment
      ? { fulfilmentByCart: { ...state.fulfilmentByCart, [cartKey]: fulfilment } }
      : {}),
  });
}

// Enabler E8-G (ADO-3109), AC16: a read of a cart landed with what the cart kept from its latest
// send: seed the edit buffer of that cart where it holds nothing. Story 8.15 (ADO-2024), D-5: a
// read of the session cart that finds it armed seeds first, so on an armed cart the review starts
// from the arming, not from what the cart kept. An arm's own answer (it lands while the write is
// pending) seeds nothing: it holds what this review already shows, and seeding it would hold the
// Fulfilment card's untouched defaults.
useBasketStore.subscribe((basket, previousBasket) => {
  const armed = basket.autoSend;
  if (
    armed !== previousBasket.autoSend &&
    armed !== null &&
    previousBasket.autoSendPending === null
  ) {
    const armedCartKey = sessionCartKey();
    if (armedCartKey !== null) seedArmedChoices(armedCartKey, armed);
  }
  const kept = basket.keptFromSend;
  if (kept === previousBasket.keptFromSend) return;
  // A reset (a change of cart, or the re-read after a send) forgets a date still waiting for the
  // options: the next read of the cart says again what it kept.
  if (kept === null) {
    keptDeliveryDateByCart.clear();
    return;
  }
  const { cartKey, customerOrderReference, requestedDeliveryDate } = kept;
  const state = useOrderSubmissionStore.getState();
  if (
    customerOrderReference !== null &&
    state.customerOrderReferenceByCart[cartKey] === undefined
  ) {
    useOrderSubmissionStore.setState({
      customerOrderReferenceByCart: {
        ...state.customerOrderReferenceByCart,
        [cartKey]: customerOrderReference,
      },
    });
  }
  if (requestedDeliveryDate === null || state.fulfilmentByCart[cartKey] !== undefined) {
    keptDeliveryDateByCart.delete(cartKey);
    return;
  }
  keptDeliveryDateByCart.set(cartKey, requestedDeliveryDate);
  seedKeptDeliveryDate(cartKey);
});

subscribeToFulfilmentOptions(() => {
  for (const cartKey of [...keptDeliveryDateByCart.keys()]) seedKeptDeliveryDate(cartKey);
});

useBasketStore.subscribe((basket, previousBasket) => {
  const submission = useOrderSubmissionStore.getState();
  // Filling the cart again (a line lands in the basket) retires the confirmation.
  if (basket.lines.length > 0 && previousBasket.lines.length === 0 && submission.phase === 'sent') {
    submission.clear();
    return;
  }
  // The Shopper changing the cart (a line edit starts, a previous order is being added, or the
  // cart is being cleared) retires a refusal: its message was about the cart as it was. Lines
  // arriving from a load (the `cart-changed` re-open) are not a change, so the refusal stays for
  // that cart. A retryable failure stays too, with its key: "Try again" still replays that
  // attempt while the basket is at the revision it sent (`submit` mints a new key once a write
  // moved it). This does not `clear()`, which would also stop an in-flight re-open from loading
  // its lines.
  const editStarted =
    basket.pending.size > previousBasket.pending.size ||
    (basket.addingFromOrder && !previousBasket.addingFromOrder) ||
    (basket.clearing && !previousBasket.clearing);
  if (editStarted && submission.phase === 'failed' && submission.error?.retryable === false) {
    useOrderSubmissionStore.setState({ phase: 'idle', error: null });
  }
});
